import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  ChangeDetectionStrategy
} from "@angular/core";
import { FormControl, FormGroup, AbstractControl } from "@angular/forms";
import { BHR, isBHRDay, BHRDay } from "@onsip/common/interfaces/bhr";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { TopicFormService } from "../../services/topic-form.service";
import { bhrDayValidator, bhrTimeRequiredValidator } from "./bhr.validator";
import BHRPickerData from "./bhr-picker-template-data";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

interface BhrFormGroup extends FormGroup {
  controls: {
    [key: string]: any;
  };
} // typing hack

@Component({
  selector: "onsip-bhr-picker",
  templateUrl: "./bhr-picker.component.html",
  styleUrls: ["./bhr-picker.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BhrPickerComponent implements OnInit, OnChanges, OnDestroy {
  /** BHR from config */
  @Input() initialValue: BHR | undefined;
  /** inverse of bhr.enabled to be bound to checkbox */
  allHours = true;
  /** Parent BHR form */
  // Create master form object - do this only once, when things change use setValue
  bhrForm = new FormGroup({
    enabled: new FormControl(),
    timezone: new FormControl()
  }) as BhrFormGroup;
  /** Days of week */
  dayOptions: Array<keyof BHR> = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
  ];
  dayOptionsTranslated: Record<string, string> = {};
  /** imported dropdown timezone menu options. */
  timezoneOptions: Array<{ value: string; text: string; code: string }> = [];

  private unsubscribe: Subject<void> = new Subject<void>();

  /** Returns default BHR object, used whenever new bhr being created */
  private static defaultBHR(): BHR {
    return {
      enabled: false,
      timezone: "-5",
      monday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: false },
      tuesday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: false },
      wednesday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: false },
      thursday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: false },
      friday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: false },
      saturday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: true },
      sunday: { start: { h: 9, m: 0 }, end: { h: 17, m: 0 }, disabled: true }
    };
  }

  constructor(private topicForm: TopicFormService, private translate: TranslateService) {}

  ngOnInit() {
    this.timezoneOptions = BHRPickerData.timezoneOptions.map(option => {
      option.text = this.translate.instant(`ONSIP_I18N.TIMEZONE.${option.code}`);
      return option;
    });

    this.dayOptionsTranslated = {
      monday: this.translate.instant("ONSIP_I18N.MONDAY"),
      tuesday: this.translate.instant("ONSIP_I18N.TUESDAY"),
      wednesday: this.translate.instant("ONSIP_I18N.WEDNESDAY"),
      thursday: this.translate.instant("ONSIP_I18N.THURSDAY"),
      friday: this.translate.instant("ONSIP_I18N.FRIDAY"),
      saturday: this.translate.instant("ONSIP_I18N.SATURDAY"),
      sunday: this.translate.instant("ONSIP_I18N.SUNDAY")
    };

    this.dayOptions.forEach(day => {
      this.bhrForm.addControl(
        day,
        new FormGroup({
          start: new FormControl(),
          end: new FormControl(),
          disabled: new FormControl(false)
        })
      );

      // HACK: propegate BHR Day errors to its start and end controls
      const fg = this.bhrForm.controls[day];
      fg.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        if (fg instanceof FormGroup) {
          fg.controls.start.updateValueAndValidity({ emitEvent: false }); // without emitEvent: false will infinite loop
          fg.controls.end.updateValueAndValidity({ emitEvent: false });
          const startErrors = { ...fg.errors, ...fg.controls.start.errors }; // merged parent and child form errors
          const endErrors = { ...fg.errors, ...fg.controls.end.errors };
          // eslint-disable-next-line no-null/no-null
          fg.controls.start.setErrors(Object.keys(startErrors).length ? startErrors : null); // set child errors as merged parent and child
          // eslint-disable-next-line no-null/no-null
          fg.controls.end.setErrors(Object.keys(endErrors).length ? endErrors : null);
        }
      });
    });

    // If there is a bhr passed from config, set values in the form
    if (this.initialValue) {
      this.patchForm(this.initialValue); // from config
    } else {
      this.patchForm(BhrPickerComponent.defaultBHR()); // default
    }
    this.topicForm.addControl("bhr", this.bhrForm);
  }

  ngOnChanges() {
    // If there is a bhr passed from config, set values in the form
    if (this.bhrForm) {
      if (this.initialValue) {
        this.patchForm(this.initialValue); // from config
      } else {
        this.patchForm(BhrPickerComponent.defaultBHR()); // default
      }
      this.topicForm.addControl("bhr", this.bhrForm);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Gets error message for each possible error */
  getErrorMessage(control: AbstractControl): string {
    // "isBhrDay" message should only be displayed on start mat-error
    return control.hasError("bhrTimeRequired")
      ? this.translate.instant("ONSIP_I18N.OPENING_AND_CLOSING_TIME_MUST_BE_SET")
      : control.hasError("isBhrDay")
      ? this.translate.instant("ONSIP_I18N.BHR_CLOSING_TIME_MUST_BE_AFTER_OPENING")
      : "";
  }

  /** Handle day start and end disabling (reactive form preferred method) */
  toggleDisableDay(checkChange: MatCheckboxChange, day: string) {
    const dayFormGroup = this.bhrForm.controls[day];
    if (dayFormGroup instanceof FormGroup) {
      if (checkChange.checked) {
        dayFormGroup.controls.start.disable();
        dayFormGroup.controls.end.disable();
      } else {
        dayFormGroup.controls.start.enable();
        dayFormGroup.controls.end.enable();
      }
    }
  }

  /** HACK: Toggles all validators, this is so a disabled bhr will still save  */
  toggleEnableBHR(checkChange: MatCheckboxChange) {
    this.bhrForm.controls.enabled.setValue(!checkChange.checked); // reverse
    this.dayOptions.forEach(day => {
      this.bhrForm.controls[day].setValidators(
        // eslint-disable-next-line no-null/no-null
        checkChange.checked ? null : [bhrTimeRequiredValidator(), bhrDayValidator()]
      );
      const dayFormGroup = this.bhrForm.controls[day];
      dayFormGroup.updateValueAndValidity({ emitEvent: false });
      if (dayFormGroup instanceof FormGroup) {
        dayFormGroup.controls.start.updateValueAndValidity({ emitEvent: false });
        dayFormGroup.controls.end.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  /** Takes a BHR object and applies values to this.bhrForm */
  private patchForm(bhr: BHR) {
    this.allHours = !bhr.enabled;
    this.bhrForm.controls.enabled.setValue(bhr.enabled);
    this.bhrForm.controls.timezone.setValue(bhr.timezone);
    this.dayOptions.forEach(day => {
      const dayFormGroup = this.bhrForm.controls[day];
      if (dayFormGroup instanceof FormGroup) {
        let currentBHRDay: BHRDay = {
          start: { h: 9, m: 0 },
          end: { h: 17, m: 0 },
          disabled: false
        }; // fallback
        if (isBHRDay(bhr[day])) {
          currentBHRDay = bhr[day] as BHRDay; // set bhr day to stored bhr day if valid
        }
        // set values for bhr day
        dayFormGroup.controls.start.setValue(currentBHRDay.start);
        dayFormGroup.controls.end.setValue(currentBHRDay.end);
        dayFormGroup.controls.disabled.setValue(currentBHRDay.disabled);
        // set validators for bhr day. HACK: no validators for bhr not enabled
        dayFormGroup.setValidators([bhrTimeRequiredValidator(), bhrDayValidator()]);
        // set disabled
        if (currentBHRDay.disabled) {
          dayFormGroup.controls.start.disable();
          dayFormGroup.controls.end.disable();
        } else {
          dayFormGroup.controls.start.enable();
          dayFormGroup.controls.end.enable();
        }
      }
    });
  }
}
